import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wallet, AlertTriangle, CircleDollarSign, History, RefreshCw,
  Loader2, Info, ArrowLeft, TrendingUp, Shield,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// ── Costanti ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

const TYPE_LABELS = {
  sale_credit:      { label: 'Vendita',           color: 'text-green-700',  bg: 'bg-green-50',   icon: '↑' },
  platform_fee:     { label: 'Commissione',        color: 'text-orange-700', bg: 'bg-orange-50',  icon: '↓' },
  payout:           { label: 'Payout',             color: 'text-blue-700',   bg: 'bg-blue-50',    icon: '↓' },
  refund:           { label: 'Rimborso',            color: 'text-red-700',    bg: 'bg-red-50',     icon: '↓' },
  dispute_hold:     { label: 'Hold contestazione', color: 'text-yellow-800', bg: 'bg-yellow-50',  icon: '⚠' },
  dispute_release:  { label: 'Contest. vinta',     color: 'text-green-700',  bg: 'bg-green-50',   icon: '✓' },
  dispute_loss:     { label: 'Contest. persa',     color: 'text-red-700',    bg: 'bg-red-50',     icon: '✗' },
  adjustment:       { label: 'Rettifica',          color: 'text-gray-700',   bg: 'bg-gray-50',    icon: '~' },
};

const STATUS_LABELS = {
  confirmed: { label: 'Confermato', cls: 'bg-green-100 text-green-700' },
  pending:   { label: 'In attesa',  cls: 'bg-yellow-100 text-yellow-700' },
  cancelled: { label: 'Annullato',  cls: 'bg-gray-100 text-gray-500' },
};

const TYPE_FILTER_OPTIONS = [
  { value: '',                label: 'Tutti i movimenti' },
  { value: 'sale_credit',     label: 'Vendite' },
  { value: 'platform_fee',    label: 'Commissioni' },
  { value: 'payout',          label: 'Payout' },
  { value: 'refund',          label: 'Rimborsi' },
  { value: 'dispute_hold',    label: 'Hold contestazioni' },
  { value: 'dispute_release', label: 'Contestazioni vinte' },
  { value: 'dispute_loss',    label: 'Contestazioni perse' },
];

// ── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (v) => {
  if (v == null) return '—';
  const abs = Math.abs(v).toFixed(2);
  return `${v >= 0 ? '+' : '-'}€${abs}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ── Componente principale ────────────────────────────────────────────────────

export default function VendorFinanceDashboard() {
  const navigate = useNavigate();

  const [balance, setBalance] = useState(null);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [balanceLoading, setBalanceLoading] = useState(true);

  const loadBalance = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const res = await api.get('/vendor/finance/balance');
      setBalance(res.data);
    } catch {
      toast.error('Errore nel caricamento del saldo');
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  const loadEntries = useCallback(async (p = 1, typeF = typeFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, page_size: PAGE_SIZE });
      if (typeF) params.set('type', typeF);
      const res = await api.get(`/vendor/finance/ledger?${params}`);
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch {
      toast.error('Errore nel caricamento dei movimenti');
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    loadBalance();
    loadEntries(1, typeFilter);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (newType) => {
    setTypeFilter(newType);
    loadEntries(1, newType);
  };

  const handleRefresh = () => {
    loadBalance();
    loadEntries(page, typeFilter);
  };

  const isNegativeTotal = balance && balance.total_balance < 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/produttore/dashboard')}
            className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Wallet className="w-6 h-6 text-primary" />
              Dashboard Finanziaria
            </h1>
            <p className="text-sm text-text-secondary mt-0.5">
              Registro movimenti e saldo del tuo conto produttore
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} className="gap-2">
          <RefreshCw className="w-3.5 h-3.5" />
          Aggiorna
        </Button>
      </div>

      {/* Banner saldo negativo */}
      {isNegativeTotal && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800">Saldo totale negativo</p>
            <p className="text-sm text-red-700 mt-0.5">
              Il tuo saldo include contestazioni o rimborsi non ancora coperti.
              I prossimi pagamenti verranno applicati a coprire il debito.
            </p>
          </div>
        </div>
      )}

      {/* Cards saldo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <BalanceCard
          icon={<TrendingUp className="w-4 h-4 text-green-600" />}
          label="Disponibile"
          sublabel="Movimenti confermati"
          value={balance?.available_balance}
          loading={balanceLoading}
          colorClass={balance?.available_balance < 0 ? 'text-red-600' : 'text-green-700'}
        />
        <BalanceCard
          icon={<Shield className="w-4 h-4 text-yellow-600" />}
          label="In revisione"
          sublabel="Hold contestazioni aperte"
          value={balance ? Math.abs(balance.hold_balance) : null}
          loading={balanceLoading}
          prefix="−€"
          colorClass="text-yellow-700"
        />
        <BalanceCard
          icon={<CircleDollarSign className="w-4 h-4 text-gray-600" />}
          label="Saldo totale"
          sublabel="Disponibile + revisione"
          value={balance?.total_balance}
          loading={balanceLoading}
          colorClass={isNegativeTotal ? 'text-red-600' : 'text-gray-800'}
        />
      </div>

      {/* Tabella movimenti */}
      <div className="bg-white border border-border rounded-xl overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-b border-border">
          <h2 className="font-semibold flex items-center gap-2">
            <History className="w-4 h-4" />
            Movimenti finanziari
            {total > 0 && (
              <span className="text-xs text-text-muted font-normal">({total} totali)</span>
            )}
          </h2>
          <select
            value={typeFilter}
            onChange={e => handleFilterChange(e.target.value)}
            className="text-sm border border-border rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            {TYPE_FILTER_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16">
            <Info className="w-10 h-10 mx-auto text-text-muted mb-3" />
            <p className="text-text-secondary text-sm">Nessun movimento trovato</p>
            {typeFilter && (
              <button
                onClick={() => handleFilterChange('')}
                className="mt-2 text-xs text-blue-600 hover:underline"
              >
                Rimuovi filtro
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs text-text-muted font-medium">Data</th>
                    <th className="text-left px-4 py-2.5 text-xs text-text-muted font-medium">Tipo</th>
                    <th className="text-left px-4 py-2.5 text-xs text-text-muted font-medium">Riferimento</th>
                    <th className="text-right px-4 py-2.5 text-xs text-text-muted font-medium">Importo</th>
                    <th className="text-right px-4 py-2.5 text-xs text-text-muted font-medium">Saldo dopo</th>
                    <th className="text-left px-4 py-2.5 text-xs text-text-muted font-medium">Stato</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {entries.map(entry => {
                    const ti = TYPE_LABELS[entry.type] || { label: entry.type, color: 'text-gray-700', bg: 'bg-gray-50', icon: '' };
                    const si = STATUS_LABELS[entry.status] || { label: entry.status, cls: 'bg-gray-100 text-gray-500' };
                    const isPos = entry.amount >= 0;
                    const isDisputeHold = entry.type === 'dispute_hold';

                    return (
                      <tr
                        key={entry.id}
                        className={`hover:bg-muted/20 transition-colors ${isDisputeHold ? 'bg-yellow-50/50' : ''}`}
                      >
                        <td className="px-4 py-3 text-xs text-text-muted whitespace-nowrap">
                          {fmtDate(entry.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${ti.bg} ${ti.color}`}>
                            <span aria-hidden="true">{ti.icon}</span>
                            {ti.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-text-secondary max-w-[200px]">
                          <span className="truncate block">{entry.reference || '—'}</span>
                          {isDisputeHold && (
                            <span className="text-yellow-700 font-medium flex items-center gap-1 mt-0.5">
                              <AlertTriangle className="w-3 h-3" />
                              Contestazione aperta
                            </span>
                          )}
                        </td>
                        <td className={`px-4 py-3 text-right font-mono font-semibold ${isPos ? 'text-green-700' : 'text-red-600'}`}>
                          {fmt(entry.amount)}
                        </td>
                        <td className={`px-4 py-3 text-right font-mono text-xs ${
                          entry.balance_after == null ? 'text-text-muted' :
                          entry.balance_after < 0 ? 'text-red-500' : 'text-text-secondary'
                        }`}>
                          {entry.balance_after != null ? `€${entry.balance_after.toFixed(2)}` : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${si.cls}`}>
                            {si.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginazione */}
            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border text-xs text-text-muted">
                <span>
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} di {total}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => loadEntries(page - 1)}
                    disabled={page === 1 || loading}
                    className="p-1.5 border rounded hover:bg-muted/30 disabled:opacity-40"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <span className="px-3 py-1 font-medium">Pag. {page}</span>
                  <button
                    onClick={() => loadEntries(page + 1)}
                    disabled={page * PAGE_SIZE >= total || loading}
                    className="p-1.5 border rounded hover:bg-muted/30 disabled:opacity-40"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Note informative */}
      <div className="bg-muted/30 rounded-xl p-4 text-xs text-text-secondary space-y-1">
        <p><strong>Saldo disponibile:</strong> somma di tutti i movimenti confermati (vendite, commissioni, payout, rimborsi).</p>
        <p><strong>In revisione:</strong> importi bloccati da contestazioni (chargeback) ancora aperte.</p>
        <p><strong>Saldo dopo:</strong> snapshot del saldo totale immediatamente dopo ogni movimento.</p>
        <p>I movimenti in stato <em>In attesa</em> (vendite entro la finestra di recesso) non concorrono al saldo disponibile.</p>
      </div>
    </div>
  );
}

// ── BalanceCard ──────────────────────────────────────────────────────────────

function BalanceCard({ icon, label, sublabel, value, loading, colorClass, prefix }) {
  const displayValue = () => {
    if (loading || value == null) return '—';
    if (prefix) return `${prefix}${Math.abs(value).toFixed(2)}`;
    return `${value >= 0 ? '' : '-'}€${Math.abs(value).toFixed(2)}`;
  };

  return (
    <div className="bg-white border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 text-xs text-text-muted mb-2">
        {icon}
        {label}
      </div>
      {loading ? (
        <div className="h-8 w-24 bg-muted/50 rounded animate-pulse" />
      ) : (
        <p className={`text-2xl font-bold ${colorClass}`}>{displayValue()}</p>
      )}
      <p className="text-xs text-text-muted mt-1">{sublabel}</p>
    </div>
  );
}
